"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Compositor = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_error_1 = require("../../streem_error");
const logger_1 = require("../../util/logger");
const sdk_debug_1 = __importDefault(require("../../util/toggles/sdk_debug"));
class Compositor {
    constructor(config) {
        this.config = config;
        this.cameraFrameSubject = new rxjs_1.Subject();
        this.renderSignalSubject = new rxjs_1.Subject();
        // cameraFrame must start with undefined for SDKs that do not produce a lightshow
        this.cameraFrame = this.cameraFrameSubject.asObservable().pipe((0, operators_1.startWith)(undefined));
        this.renderSignal = this.renderSignalSubject.asObservable();
        const canvas = document.createElement('canvas');
        const bufferCtx = canvas.getContext('2d');
        if (!bufferCtx) {
            logger_1.logger.error('Unable to capture 2D rendering context from canvas.');
            throw new streem_error_1.StreemError('Unable to capture 2D rendering context for video feed.');
        }
        this.buffer = canvas;
        this.bufferCtx = bufferCtx;
        this.stats = config.stats;
    }
    /**
     * connect will start an animation loop and use the chosen compositor to
     * combine visual elements from separate sources into single images. These single images
     * are rendered onto a buffer canvas. The current buffer canvas is exposed as readonly.
     *
     * connect returns a function which will stop the compositing the video feed onto the buffer canvas.
     */
    connect() {
        const renderingUnsubscriber = this.config.videoStarter.startRenderingVideoFrames((videoSource, cameraFrame) => {
            this.videoFrameCallback(videoSource, cameraFrame);
        });
        const disconnectFpsPanel = sdk_debug_1.default.onChange('fps', value => {
            if (value) {
                this.stats.showPanel(1); // 0: fps, 1: ms, 2: mb, 3+: custom
                this.stats.addPanel();
            }
            else if (!value && this.stats.getParentElement() === document.body) {
                this.stats.removePanel();
            }
        });
        return () => {
            if (renderingUnsubscriber) {
                renderingUnsubscriber();
            }
            if (disconnectFpsPanel) {
                disconnectFpsPanel();
            }
            if (this.stats.getParentElement() === document.body) {
                this.stats.removePanel();
            }
            if (this.stopCompositingHook) {
                this.stopCompositingHook();
            }
        };
    }
    /**
     * stopVideoHook will be called when video source is not enabled.
     */
    stopCompositingHook() { }
    /**
     * This is the bound callback method we pass to the VideoScheduler that is called on each frame of the video.
     */
    videoFrameCallback(source, latestCameraFrame) {
        this.stats.begin();
        // Publish the latestCameraFrame if it is defined.
        if (latestCameraFrame) {
            this.cameraFrameSubject.next(latestCameraFrame);
        }
        // Resize the buffer canvas if necessary.
        if (source instanceof HTMLVideoElement) {
            if (this.buffer.width !== source.videoWidth ||
                this.buffer.height !== source.videoHeight) {
                this.buffer.width = source.videoWidth;
                this.buffer.height = source.videoHeight;
            }
        }
        else {
            if (this.buffer.width !== source.width || this.buffer.height !== source.height) {
                this.buffer.width = source.width;
                this.buffer.height = source.height;
            }
        }
        // Draw the frame onto the buffer canvas.
        this.bufferCtx.drawImage(source, 0, 0);
        // Perform post-processing on the buffer canvas.
        const context = {
            videoSource: source,
            buffer: this.buffer,
            latestCameraFrame,
        };
        this.postProcessingHook(context);
        this.stats.end();
        // Signal to out to our external rendering system that the next frame is ready.
        this.renderSignalSubject.next(undefined);
    }
}
exports.Compositor = Compositor;
//# sourceMappingURL=compositor.js.map